import { useEffect, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'

import { API } from '@/api'
import { TeamMemberVolumeStat } from '@/api/type-response'
import { Volume } from '@/api/type-server'
import { VolumeUsageSegmentedBar } from '@/pages/space-mgr/components/volume-usage-segmented-bar'
import { useTeamStore } from '@/stores/team'
import { formatDatePreset, formatToDynamicDecimalPlaces } from '@/utils/formatter'

export function StorageUsageBar({ memberId }: { memberId: string }) {
  const [snapshotVolumes, setSnapshotVolumes] = useState<Volume[]>()
  const [cloudVolumes, setCloudVolumes] = useState<Volume[]>()
  const [imageVolumes, setImageVolumes] = useState<Volume[]>()
  const [partVolumeQuotaInGB, setPartVolumeQuotaInGB] = useState(0)
  const [memberVolumeStat, setMemberVolumeStat] = useState<TeamMemberVolumeStat | null>(null)
  const teamStore = useTeamStore()

  const loadData = async () => {
    try {
      // Fetch member volumes
      const volumesRes = await API.getMemberVolumes({ res_id: memberId })

      if (volumesRes?.volumes) {
        const volumes = volumesRes.volumes

        // allocPart is the total member storage
        setPartVolumeQuotaInGB(volumes.allocPart?.quota_gb || 0)

        // subParts are the subdivisions (snapshot/datadrive by region)
        if (volumes.subParts) {
          setSnapshotVolumes(volumes.subParts.filter((v: Volume) => v.type === 2))
          setCloudVolumes(volumes.subParts.filter((v: Volume) => v.type === 1))
          setImageVolumes(volumes.subParts.filter((v: Volume) => v.type === 3))
        }
      }
      const teamResID = teamStore.teamTokenData?.teamResID
      if (teamResID) {
        const statsRes = await API.getTeamMemberVolumeStats({
          res_id: teamResID,
          member_res_ids: [memberId],
          page: 1,
          per_page: 1,
        })

        if (statsRes?.volumes && statsRes.volumes.length > 0) {
          setMemberVolumeStat(statsRes.volumes[0])
        }
      }
    } catch (error) {
      console.error('Failed to load member space data:', error)
    }
  }

  useEffect(() => {
    loadData()
  }, [memberId])

  const totalActualUsageInGB = useMemo(() => {
    const allVolumes = [
      ...(snapshotVolumes || []),
      ...(cloudVolumes || []),
      ...(imageVolumes || []),
    ]
    return allVolumes.reduce((sum, volume) => sum + volume.usage_mb / 1024, 0)
  }, [snapshotVolumes, cloudVolumes, imageVolumes])

  const hasSnapshotVolumes = (snapshotVolumes?.length ?? 0) > 0
  const hasImageVolumes = (imageVolumes?.length ?? 0) > 0

  return (
    <div className="border border-neutral-200 p-8 mb-8 rounded-2xl">
      <div className="t-sb mb-4">
        <Trans i18nKey="space.titleSpaceStorage">Storage Space</Trans>
      </div>
      {partVolumeQuotaInGB === 0 ? (
        <div className="h-2 bg-neutral-100 rounded-full animate-pulse" />
      ) : (
        <VolumeUsageSegmentedBar
          snapshotVolumes={snapshotVolumes}
          cloudVolumes={cloudVolumes}
          totalQuota={partVolumeQuotaInGB}
          imageOnSpaceBilling={hasImageVolumes}
          snapshotOnSpaceBilling={hasSnapshotVolumes}
        />
      )}
      <div className="flex justify-between items-center mt-4">
        <div className="flex gap-4 items-center">
          <div className="text-secondary t-sb">
            <Trans
              i18nKey="space.usageFormat"
              values={{
                usage: formatToDynamicDecimalPlaces(totalActualUsageInGB),
                quota: formatToDynamicDecimalPlaces(partVolumeQuotaInGB),
              }}
              components={[
                <span className="text-primary" key="usage" />,
                <span className="text-primary" key="quota" />,
              ]}
            />
          </div>
          <div className="h-3 w-0.5 bg-neutral-200" />

          {/* Expiration + Upgrade */}
          <div className="flex items-center gap-4 t-sb">
            <div className="text-secondary">
              <Trans i18nKey="space.expireOn">Expires on</Trans>{' '}
              <span className="text-primary">
                {memberVolumeStat?.storageStat?.expireTime
                  ? formatDatePreset(memberVolumeStat.storageStat.expireTime.seconds * 1000)
                  : '-'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 t-sm">
          {hasSnapshotVolumes && (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-btn-tertiary" />
              <span>
                <Trans i18nKey="space.volumeItemSubTitleSnapshot">Snapshot</Trans>
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-btn-secondary" />
            <span>
              <Trans i18nKey="space.volumeItemSubTitleCloudStorage">Datadrive</Trans>
            </span>
          </div>
          {hasImageVolumes && (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-active" />
              <span>
                <Trans i18nKey="space.volumeItemSubTitleImage">Image</Trans>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
